/**
 * clauseTextMarkers — the single parser for clause display markers.
 *
 * Clause option text uses an inline marker grammar produced on the backend by
 * the template extraction pipeline, derived automatically when a template
 * version is activated. The admin UI renders it read-only:
 *   [HEADING]…[/HEADING]   section heading (extraction puts one at the start)
 *   [GUIDANCE]…[/GUIDANCE] drafting note (displayed as a callout, stripped at
 *                          the generation boundary by the backend sanitiser)
 *   [TABLE]…[/TABLE]       pipe-delimited rows, one row per line
 *   <<placeholder>>        inline fill-in badge (handled at render time)
 *
 * This module is deliberately neutral (utils/, not ContractWizard/) — the
 * wizard preview AND the clause admin's read-only preview both consume it.
 *
 * Malformed-marker policy (bound to the backend generation stripper
 * `backend/src/utils/displayMarkers.ts` via the shared fixture
 * `tests/shared/markerFixtures/displayMarkers.json`): generation is
 * authoritative, so a stray/unmatched marker TOKEN is removed and the
 * surrounding content kept — the preview reflects what will actually ship
 * rather than rendering the token literally. Non-greedy pairing consumes the
 * first well-formed block; any stray token left inside or around it (a nested
 * opener, a trailing closer) is stripped. An empty guidance block is dropped.
 */

export type Segment =
  | { kind: 'heading'; text: string }
  | { kind: 'guidance'; text: string }
  | { kind: 'paragraph'; text: string }
  | TableSegment;

export interface TableSegment {
  kind: 'table';
  /** rows → cells → the cell's parsed segments (guidance/placeholders inside
   *  cells are parsed once here, never re-parsed from raw strings). */
  rows: Segment[][][];
}

const TABLE_BLOCK = /(\[TABLE\][\s\S]*?\[\/TABLE\])/;
const INLINE_BLOCK = /\[(HEADING|GUIDANCE)\]([\s\S]*?)\[\/\1\]/g;
const LEADING_HEADING = /^\s*\[HEADING\]([\s\S]*?)\[\/HEADING\]\n?/;
const STRAY_MARKER_TOKEN = /\[\/?(?:HEADING|GUIDANCE|TABLE)\]/g;

/**
 * Remove any residual marker token (an unmatched opener/closer, or a stray
 * token left inside an extracted block) and collapse the doubled space a
 * mid-line removal leaves behind. This is the same conservative treatment the
 * backend generation stripper applies to malformed tokens, keeping the preview
 * and the generated document in agreement (bound by the shared fixture).
 */
function stripStrayTokens(text: string): string {
  if (!text.includes('[')) return text;
  return text.replace(STRAY_MARKER_TOKEN, '').replace(/ {2,}/g, ' ');
}

/** Trim outer blank lines from a paragraph chunk, preserving inner structure. */
function trimChunk(text: string): string {
  return text.replace(/^\n+/, '').replace(/\n+$/, '').trim();
}

/** Parse text that contains no [TABLE] blocks into heading/guidance/paragraph segments. */
function parseInline(text: string): Segment[] {
  const segments: Segment[] = [];
  let lastIndex = 0;
  INLINE_BLOCK.lastIndex = 0;
  for (const match of text.matchAll(INLINE_BLOCK)) {
    const before = trimChunk(stripStrayTokens(text.slice(lastIndex, match.index)));
    if (before) segments.push({ kind: 'paragraph', text: before });
    const content = stripStrayTokens(match[2]).trim();
    if (match[1] === 'HEADING') {
      segments.push({ kind: 'heading', text: content });
    } else if (content) {
      // Empty guidance blocks are dropped
      segments.push({ kind: 'guidance', text: content });
    }
    lastIndex = (match.index ?? 0) + match[0].length;
  }
  const after = trimChunk(stripStrayTokens(text.slice(lastIndex)));
  if (after) segments.push({ kind: 'paragraph', text: after });
  return segments;
}

/** Parse a well-formed [TABLE]…[/TABLE] block into structured rows. */
function parseTable(block: string): TableSegment {
  const inner = block
    .replace(/^\[TABLE\]\n?/, '')
    .replace(/\n?\[\/TABLE\]$/, '');
  const rows = inner
    .split('\n')
    .filter((row) => row.trim())
    .map((row) => row.split(' | ').map((cell) => parseInline(cell.trim())));
  return { kind: 'table', rows };
}

/**
 * Parse clause option text into structural segments. The ONE marker parser —
 * `formatClauseText` and the clause preview model both consume it.
 */
export function parseClauseSegments(text: string): Segment[] {
  if (!text) return [];
  const segments: Segment[] = [];
  for (const part of text.split(TABLE_BLOCK)) {
    if (part.startsWith('[TABLE]') && part.endsWith('[/TABLE]')) {
      segments.push(parseTable(part));
    } else {
      segments.push(...parseInline(part));
    }
  }
  return segments;
}

/**
 * Split a leading [HEADING] off the text. Used by the preview model's
 * conditional heading dedupe (identical headings hoist to clause level).
 */
export function stripLeadingHeading(text: string): {
  heading: string | null;
  body: string;
} {
  if (!text) return { heading: null, body: '' };
  const match = text.match(LEADING_HEADING);
  if (!match) return { heading: null, body: text };
  return { heading: match[1].trim(), body: text.slice(match[0].length) };
}

/** Count guidance segments in a text, including those inside table cells. */
export function countGuidanceSegments(text: string): number {
  const countIn = (segments: Segment[]): number =>
    segments.reduce((total, segment) => {
      if (segment.kind === 'guidance') return total + 1;
      if (segment.kind === 'table') {
        return (
          total +
          segment.rows.reduce(
            (rowTotal, row) =>
              rowTotal + row.reduce((cellTotal, cell) => cellTotal + countIn(cell), 0),
            0
          )
        );
      }
      return total;
    }, 0);
  return countIn(parseClauseSegments(text));
}
